import { stores } from '@kurtosys/app-start';
import { components } from '@kurtosys/ksys-app-components';
import { common, query } from '@kurtosys/ksys-app-template';
import { IInputs } from '@kurtosys/types/app/index.js';
import { IManifest } from '@kurtosys/types/appsManager/index.js';
import { action, computed, makeObservable, observable } from 'mobx';

import { TStoreContext } from '../../../models';
import { IComponentStyles } from '../../../models/app/IComponentStyles.js';
import { IConfiguration } from '../../../models/app/IConfiguration.js';
import { AppStore } from '../../App/stores/AppStore.js';
import { IAppTabComponent } from '../models/IAppTabComponent.js';
import { IAppTabsConfiguration } from '../models/IAppTabsConfiguration.js';
import { IAppTabsProps } from '../models/IAppTabsProps.js';

export class TabsStore extends stores.base.StoreBase<IConfiguration, IComponentStyles> {
	static componentKey = 'tabs' as const;
	appStore: AppStore;
	eventBusHelper: common.helpers.EventBusHelper;
	@observable activeTab: string | undefined;

	constructor(storeContext: TStoreContext, manifest: IManifest) {
		super(storeContext, manifest);
		this.appStore = storeContext.get<AppStore>('appStore');
		this.eventBusHelper = new common.helpers.EventBusHelper(this.appStore.applicationGuid);

		makeObservable(this);
	}

	@computed
	get configuration(): IAppTabsConfiguration | undefined {
		const appStore = this.storeContext.get<AppStore>('appStore');
		if (appStore) {
			return appStore.getComponentConfiguration(TabsStore.componentKey);
		}
	}

	@computed
	get contentLoadDelay(): number {
		return this.configuration.contentLoadDelay || 500;
	}

	@computed
	get tabInputs(): components.base.ApplicationEmbed.models.IApplicationEmbedInput[] {
		if (this.appStore && this.appStore.appParamsHelper) {
			const { appParamsHelper } = this.appStore;

			if (appParamsHelper.inputs) {
				const { inputs } = appParamsHelper;
				const inputKeys = Object.keys(inputs);

				return inputKeys.map((key: string) => {
					return {
						key,
						value: inputs[key as keyof IInputs],
					};
				});
			}
		}
		return [];
	}

	@computed
	get activeTabIndex(): number {
		return this.tabKeys.indexOf(this.tabName);
	}

	@computed
	get tabGroup(): string | undefined {
		const { appCardProps } = this.appStore;
		return appCardProps && appCardProps.title;
	}

	@computed
	get tabKeys(): string[] {
		const { tabComponents } = this.tabsProps;
		return tabComponents.map((tabComponent) => tabComponent.tabKey);
	}

	@computed
	get tabName(): string | undefined {
		return this.activeTab;
	}

	@action
	setActiveTab = (activeTab: string) => {
		this.activeTab = activeTab;
	};

	onChange = (activeTab: string) => {
		const { analyticsHelper, urlRewrite, appParamsHelper } = this.appStore;
		if (!common.commonUtils.isNullOrEmpty(urlRewrite)) {
			const { defaultUrl, base } = urlRewrite;
			const redirectHelper = new common.helpers.RedirectHelper(
				{ transformClass: query.transform.Transform, queryClass: query.Query },
				defaultUrl,
				base,
			);
			const payload = {
				activeTab: activeTab || '',
				...appParamsHelper.inputs,
			};
			const newUrl = redirectHelper.getRedirectPath(urlRewrite, payload);
			if (analyticsHelper) {
				analyticsHelper.logEvent({
					event: 'tab_click',
					eventType: 'click',
					context: {
						tabName: this.tabName,
						tabGroup: this.tabGroup,
					},
				});
			}
			window.history.pushState({}, '', newUrl);
		}
	};

	orchestratePropsAndQueries(children: IAppTabComponent[]) {
		const mergedChildren: IAppTabComponent[] = children.map((child) => {
			if (child.props || child.queries) {
				const queriesProps = this.mergeQueriesAndProps(child) as IAppTabComponent['props'];

				if (queriesProps) {
					if (queriesProps.header) {
						child.header = queriesProps.header;
					}

					if (queriesProps.tabKey) {
						child.tabKey = queriesProps.tabKey;
					}
				}
			}

			return child;
		});

		return mergedChildren;
	}

	filterHiddenTabChildren(children: IAppTabComponent[]) {
		const filteredChildren: IAppTabComponent[] = [];

		children.forEach((child) => {
			const queriesProps = this.mergeQueriesAndProps(child) as IAppTabComponent['props'];
			if (queriesProps) {
				if (!queriesProps.hide) {
					filteredChildren.push(child);
				}
			}
		});

		return filteredChildren;
	}

	configureTabComponent = (tabComponent: IAppTabComponent) => {
		const asset = this.getQueryValue(tabComponent.icon?.assetQuery) || tabComponent.icon?.assetQuery || null;
		if (asset) {
			tabComponent.icon.asset = asset;
		}

		return tabComponent;
	};

	@computed
	get tabsProps(): IAppTabsProps | undefined {
		const config = this.configuration;

		if (config) {
			// Make sure to break the reference to the child objects to ensure that children.content does not get overwritten
			const children = config.children.map((child) => ({ ...child })) || [];
			const filteredChildren: IAppTabComponent[] = this.filterHiddenTabChildren(children);
			const mergeChildrenPropsAndQueries = this.orchestratePropsAndQueries(filteredChildren);
			const props: IAppTabsProps = {
				...config,
				onChange: this.onChange,
				tabComponents: mergeChildrenPropsAndQueries.map(this.configureTabComponent),
			};

			return props as IAppTabsProps;
		}

		return undefined;
	}

	handleEventBusEvents = (event) => {
		console.log('handleEventBusEvents', event);
	};

	@action
	async initialize(): Promise<void> {
		const config = this.configuration;
		const activeTabManagement = this.appStore.initialActiveTab;
		if (activeTabManagement) {
			this.activeTab = activeTabManagement;
		} else if (config?.activeTab) {
			this.activeTab = config.activeTab;
		}
	}
}
